import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { keys } from '@clubhub/constants';
import { setUser as setSentryUser } from '@sentry/angular';
import { clientIds } from '@shared/constants';
import { ClientApps } from '@shared/models';
import { AuthenticationResultStatus, IAuthenticationResult, IUser } from '@shared/models/authorization';
import { JwtHelperService } from '@shared/services/jwt-decode';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, distinctUntilChanged, firstValueFrom, from, of, startWith, tap } from 'rxjs';
import { filter, map, mergeWith } from 'rxjs/operators';
import { ConfigService } from './config.service';

const authScope = 'openid profile criteria LenderHubApi MSS.IdentityServerAPI offline_access';

const authConfig: AuthConfig = {
  responseType: 'code',
  scope: authScope,
  strictDiscoveryDocumentValidation: false,
  disableAtHashCheck: true,
  useSilentRefresh: true,
  timeoutFactor: 0.75,
  sessionChecksEnabled: true,
  showDebugInformation: false,
  clearHashAfterLogin: true,
  silentRefreshShowIFrame: false,
};

@Injectable({
  providedIn: 'root',
})
export class AuthorizeService {
  private readonly oAuthService = inject(OAuthService);
  private readonly configService = inject(ConfigService);
  private readonly router = inject(Router);
  private internalLoginConfigPromise: Promise<void> | null = null;
  private redirectToLoginPromise: Promise<boolean> | null = null;

  public user: IUser | null = null;
  public historyView = false;
  public readonly allowCriteriaPlus = true;
  public readonly tokenKey = '';
  private readonly userStorageKey = 'currentUser';
  private readonly redirectUrlStorageKey = 'authReturnUrl';
  private readonly sessionAccessTokenKey = 'access_token';
  private readonly primaryLenderNameIdKey = 'primaryLenderNameId';
  private readonly loginCallbackUrl = `${window.location.origin}/authentication/login-callback`;
  private readonly logoutCallbackUrl = `${window.location.origin}/authentication/logout-callback`;
  private readonly silentRefreshRedirectUri = `${window.location.origin}/silent-refresh.html`;

  private _isAgencyNumberRegistered: boolean | null = null;
  private readonly userSubject = new BehaviorSubject<IUser | null>(null);
  private readonly hasValidAccessToken$: Observable<boolean>;

  public constructor() {
    this.userSubject.subscribe(user => {
      setSentryUser(
        user
          ? {
            // Not adding email because it is PII, but IDs/client IDs are fine
            id: user.sub,
            externalClientId: user.sso_id,
            lenderId: user.lender,
          }
          : null,
      );
    });

    this.hasValidAccessToken$ = this.oAuthService.events.pipe(
      startWith(null),
      mergeWith(this.oAuthService.discoveryDocumentLoaded$, this.userSubject),
      map(() => this.oAuthService.hasValidAccessToken()),
    );

    // When a silent refresh fails, initiate a normal login flow
    this.oAuthService.events
      .pipe(filter(evt => ['silent_refresh_error', 'silent_refresh_timeout','token_refresh_error', 'invalid_nonce_in_state'].includes(evt.type)))
      .subscribe(() => this.redirectToLogin());

    document.addEventListener('visibilitychange', this.handleDocumentVisibilityChange.bind(this));
  }

  // Should be be called during app initialisation. Loads user before attempting to do any app logic.
  public async initialize(): Promise<void> {
    this.configure();

    if (this.oAuthService.hasValidAccessToken()) {
      await this.getCurrentUser();

      if (!this.isSsoUser) {
        await this.setupInternalLoginConfig().catch(() => undefined);
        this.safeCheckSession();
      }
    } else {
      this.setCurrentUser(null);
    }
  }

  private configure() {
    const authConfiguration = {
      ...authConfig,
      clientId: this.configService.applicationName,
      issuer: this.configService.identityServerUrl,
      redirectUri: this.loginCallbackUrl,
      postLogoutRedirectUri: this.logoutCallbackUrl,
      silentRefreshRedirectUri: this.silentRefreshRedirectUri,
      useSilentRefresh: true,
    } as AuthConfig;

    this.oAuthService.configure(authConfiguration);
  }

  public get primaryLenderNameId(): number | null {
    const id = sessionStorage.getItem(this.primaryLenderNameIdKey) ?? null;
    return id === null ? null : +id;
  }

  public async setPrimaryLenderNameId(id: number): Promise<void> {
    sessionStorage.setItem(this.primaryLenderNameIdKey, id?.toString());
    await this.refreshUser();
    this.oAuthService.refreshToken();
  }

  private async refreshUser(): Promise<IUser | null> {
    this.setCurrentUser(null);
    return this.loadUserProfile();
  }

  public get isLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  public get tokenEndpoint() {
    return `${this.configService.config?.identityServerUrl}/connect/token`;
  }

  public get isSsoUser$(): Observable<boolean | null> {
    return this.getUser().pipe(map((user: IUser | null) => !!user && user.sso_user === 'true'));
  }

  public getUserDistinct(): Observable<IUser | null> {
    return this.getUser().pipe(distinctUntilChanged((a, b) => a?.sub === b?.sub));
  }

  public setUser(user: IUser, token: string | null = null): Observable<IUser | null> {
    return this.getExternalUser(token ?? '').pipe(
      tap(u => {
        if (u?.profile) {
          const externalUser = u.profile as unknown as IUser;
          this.setCurrentUser(externalUser);
        }
      }),
    );
  }

  public hasRole(role: string): boolean {
    return (this.user?.role && (Array.isArray(this.user.role) ? this.user.role.some(x => x === role) : this.user.role === role)) ?? false;
  }

  public isAuthenticated(): Observable<boolean> {
    return this.hasValidAccessToken$;
  }

  public getAccessToken(): Observable<string | null> {
    return of(this.oAuthService.getAccessToken());
  }

  private cacheUser(user: IUser | null) {
    localStorage.setItem(this.userStorageKey, JSON.stringify(user));
    this.user = user;
  }

  private restoreCachedUser(): IUser | null {
    if (this.user) {
      return this.user;
    }
    const json = localStorage.getItem(this.userStorageKey);
    const cachedUser = json ? JSON.parse(json) : null;
    if (cachedUser) {
      this.setCurrentUser(cachedUser);
    }
    return this.user;
  }

  private async loadUserProfile(): Promise<IUser | null> {
    if (this.user) {
      return firstValueFrom(this.userSubject);
    }

    const userProfile = await this.oAuthService.loadUserProfile();
    const userProfileInfo = userProfile as Record<string, unknown> & { info?: IUser } | undefined;
    if (userProfileInfo?.info) {
      const user = userProfileInfo.info;
      this.setCurrentUser(user);
    }

    return Promise.resolve(null);
  }

  private setCurrentUser(user: IUser | null) {
    this.user = user;
    this.userSubject.next(user);
    this.cacheUser(user);
  }

  private get hasCurrentUser(): boolean {
    return !!this.user;
  }

  private async getCurrentUser(): Promise<IUser | null> {
    if (this.oAuthService.hasValidAccessToken()) {
      if (!this.hasCurrentUser) {
        const user = this.restoreCachedUser();
        if (!!user) {
          return user;
        }

        await this.loadUserProfile();
        return this.user;
      }

      return this.user;
    }

    return null;
  }

  // Previous implementation based on Observable
  public getUser(): Observable<IUser | null> {
    if (this.hasCurrentUser) {
      return this.userSubject;
    }

    from(this.getCurrentUser()).subscribe();
    return this.userSubject;
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state: unknown): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  public async signOut(state: unknown): Promise<IAuthenticationResult> {
    sessionStorage.removeItem(this.primaryLenderNameIdKey);
    this.oAuthService.logOut();
    return this.success(state);
  }

  public async completeSignOut(state: string): Promise<IAuthenticationResult> {
    this.setCurrentUser(null);
    sessionStorage.clear();
    return this.success(state);
  }

  public async signIn(state: any): Promise<IAuthenticationResult> {
    localStorage.setItem(this.redirectUrlStorageKey, JSON.stringify(state));
    this.oAuthService.initLoginFlow();

    return this.success(state);
  }

  public async completeSignIn(): Promise<IAuthenticationResult> {
    if (!this.oAuthService.hasValidAccessToken()) {
      this.oAuthService.initLoginFlow();
      return this.error('Login failed');
    }

    await this.loadUserProfile();
    const json = localStorage.getItem(this.redirectUrlStorageKey);
    const returnUrl = json ? JSON.parse(json) : null;

    localStorage.removeItem(this.redirectUrlStorageKey);
    return this.success(returnUrl);
  }

  public get isSsoUser(): boolean {
    return this.user?.sso_user === 'true';
  }

  public get isS365Client(): boolean {
    return this.user?.sso_id === clientIds.smartr365;
  }

  public get ssoClientId(): string | undefined {
    return this.user?.sso_id;
  }

  public get isClubHubApp(): boolean {
    return this.user?.app_id === ClientApps.ClubHub;
  }

  public get isIgniteApp(): boolean {
    return this.user?.app_id === ClientApps.Ignite;
  }

  public get agencyNumber() {
    const agencyNumber = parseInt(this.user?.agencyNumber ?? '');
    return isNaN(agencyNumber) ? null : agencyNumber;
  }

  public get getUserInformation() {
    return this.user;
  }

  public setAgencyNumber(newAgencyNumber: string | undefined, isRegistered: boolean | null = null) {
    if (!this.user) {
      return;
    }

    if (newAgencyNumber?.length) {
      this.user.agencyNumber = newAgencyNumber;
      sessionStorage.setItem(keys.agencyNumber, newAgencyNumber);
    } else {
      delete this.user.agencyNumber;
      sessionStorage.removeItem(keys.agencyNumber);
    }

    this._isAgencyNumberRegistered = isRegistered;
  }

  public get isAgencyNumberRegistered() {
    // All users have the Claims role regardless of whether they have an agency number or whether that broker is
    // registered, so we just return the cached value. If the cached value is null, that means we need to ask the server
    // For registered users, they only get the Claims role if their assigned broker is registered.
    return this._isAgencyNumberRegistered;
  }

  public set isAgencyNumberRegistered(value) {
    this._isAgencyNumberRegistered = this.isSsoUser ? value : null;
  }

  private getExternalUser(token: string): Observable<any> {
    if (token) {
      // Need to set external token to oAuth session
      sessionStorage.setItem(this.sessionAccessTokenKey, token);

      const service = new JwtHelperService();
      const profile = service.decodeToken(token);
      const agencyNumberInSession = sessionStorage.getItem(keys.agencyNumber);
      if (agencyNumberInSession && profile.sso_user === 'true') {
        profile.agencyNumber = agencyNumberInSession;
      }
      return of({ profile });
    }
    return of(null);
  }

  public async setupExternalLoginConfig(): Promise<void> {
    const config = {
      clientId: this.configService.applicationName,
      issuer: this.configService.identityServerUrl,

      responseType: 'client_credentials',
      scope: authScope,
      redirectUri: this.loginCallbackUrl,
      postLogoutRedirectUri: this.logoutCallbackUrl,
      strictDiscoveryDocumentValidation: false,
      disableAtHashCheck: true,
      timeoutFactor: 0.75,
      sessionChecksEnabled: true,
      clearHashAfterLogin: true,
    } as AuthConfig;

    this.oAuthService.configure(config);
    await this.oAuthService.loadDiscoveryDocument();
  }

  public async setupInternalLoginConfig(): Promise<void> {
    this.internalLoginConfigPromise ??= this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.oAuthService.setupAutomaticSilentRefresh();
    });
    return this.internalLoginConfigPromise;
  }

  private handleDocumentVisibilityChange() {
    if (document.visibilityState !== 'visible') {
      return;
    }

    // When the document is made visible (e.g. by switching back from another tab), then we immediately recheck the state of the current
    // access token and try refresh it if possible.
    // The reason for this is that the user's browser may have put this tab to sleep after long periods of inactivity and so the token may
    // not have been automatically refreshed in the background during this time, leaving the current token invalid.

    if (this.user && this.isSsoUser) {
      // SSO users' tokens do not work with oAuthService.getAccessTokenExpiration(), so need to check the expiration ourselves.
      // If it's out of date, then we need to tell them they've been logged out because it's not possible to refresh their tokens.
      if (this.user.exp * 1000 < Date.now()) {
        this.router.navigate(['/authentication/logout']);
      }
    } else if (this.user && this.oAuthService.getAccessTokenExpiration() < Date.now()) {
      // For non-SSO users, try do a silent refresh if the token has expired, and if that fails redirect to the login flow.
      this.oAuthService
        .silentRefresh()
        .then(evt => {
          if (evt instanceof OAuthErrorEvent) {
            this.redirectToLogin();
          }
        })
        .catch(() => this.redirectToLogin());
    }
  }

  public redirectToLogin() {
    this.redirectToLoginPromise ??= this.router.navigate(['/authentication/login'], {
      queryParams: {
        returnUrl: this.router.url ?? '/',
      },
    });
    return this.redirectToLoginPromise;
  }

  /**
   * checkSession can sometimes throw if the iframe has not been set up is or is being blocked.
   * This method will wait and retry up to twice (3 times total) on an error.
   */
  private safeCheckSession(retries = 2) {
    try {
      this.oAuthService.checkSession();
    } catch {
      if (retries > 0) {
        setTimeout(() => this.safeCheckSession(retries - 1), 1000);
      }
    }
  }
}
